const ACCOUNT_URL_PATHS = {
  account: '/account',
} as const;

const PUBLIC_URL_PATHS = {
  login: '/login',
  register: '/register',
  resetPassword: '/reset-password',
} as const;

export function useRoutePaths() {
  /**
   * Checks if a given path matches any path in a provided list,
   * optionally prefixed with a locale.
   * @param value - The path to check (e.g., '/de/login' or '/login')
   * @param locale - Current locale code (e.g., 'de')
   * @param paths - A record of path strings to match against
   * @returns true if the path matches any of the paths, false otherwise
   */
  const _matchesPathList = (
    value: string,
    locale: string,
    paths: Record<string, string>,
  ): boolean => {
    const normalizedValue = value.endsWith('/') ? value.slice(0, -1) : value;

    return Object.values(paths).some(
      (path) =>
        normalizedValue === path || normalizedValue === `/${locale}${path}`,
    );
  };

  /**
   * Checks if a given path is an account-related path.
   * @param value - The path to check
   * @param locale - Current locale code
   * @returns true if the path is an account path
   */
  const isAccountPath = (value: string, locale: string): boolean => {
    return _matchesPathList(value, locale, ACCOUNT_URL_PATHS);
  };

  /**
   * Checks if a given path is a public path (login, register, reset password)
   * @param value - The path to check
   * @param locale - Current locale code
   * @returns true if the path is a public path
   */
  const isPublicPath = (value: string, locale: string): boolean => {
    return _matchesPathList(value, locale, PUBLIC_URL_PATHS);
  };

  /**
   * Generates a set of fully localized paths based on the provided locale.
   * @param locale - Current locale code
   * @returns Object containing localized URLs for home, login, register, reset password, and account pages
   */
  const getLocalizedPaths = (locale: string) => {
    return {
      home: `/${locale}`,
      login: `/${locale}${PUBLIC_URL_PATHS.login}`,
      register: `/${locale}${PUBLIC_URL_PATHS.register}`,
      resetPassword: `/${locale}${PUBLIC_URL_PATHS.resetPassword}`,
      account: `/${locale}/account/order`,
    };
  };

  /**
   * Updates a URL string to include the given locale as the first path segment.
   * If the current path already contains a valid locale, it will be replaced.
   * Otherwise, the locale will be prepended.
   * @param value - Full URL string (e.g., window.location.href)
   * @param localeCode - Locale code to apply (e.g., 'en' or 'de')
   * @param availableLocaleCodes - List of all valid locale codes
   * @returns New path string with the locale properly applied
   */
  const buildLocalizedPathForUrl = (
    value: string,
    localeCode: string,
    availableLocaleCodes: string[],
  ): string => {
    const currentUrl = new URL(value);
    const pathSegments = currentUrl.pathname.split('/').filter(Boolean);

    if (
      pathSegments.length > 0 &&
      availableLocaleCodes.includes(pathSegments[0])
    ) {
      pathSegments[0] = localeCode;
    } else {
      pathSegments.unshift(localeCode);
    }

    return '/' + pathSegments.join('/');
  };

  return {
    isPublicPath,
    isAccountPath,
    getLocalizedPaths,
    buildLocalizedPathForUrl,
  };
}
